import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonSpinner,
  IonText,
  IonInputOtp,
} from '@ionic/react';
import { checkmarkCircleOutline, mailOutline } from 'ionicons/icons';
import { CLAIMS_LEVEL, ClaimsLogic } from '@/modules/idv';
import { useUserContext } from '@/modules/user/useUserContext';
import { SixDigitCode } from '@/models/six_digit_code';
import { postIdvConfirmVerification } from './idv';
import { apiCall, isNetworkError, NETWORK_ERROR_MESSAGE } from '@/util/api';
import { useAppToast } from '@/hooks/useAppToast';

import { VerificationModalCloseReason } from './PhoneVerificationModal';
import { pxToRem } from '@/modules/config/constants';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: (reason: VerificationModalCloseReason) => void;
  onSuccess?: () => void;
  continuePath: string;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  continuePath: providedContinuePath,
}) => {
  const {
    claims: contextClaims,
    email: contextEmail,
    refreshUserContext,
  } = useUserContext();

  const [presentToast, dismissToast] = useAppToast();
  const [submitting, setSubmitting] = useState(false);
  const [idvInitiated, setIdvInitiated] = useState(false);
  const [currentPage, setCurrentPage] = useState<'send' | 'code'>('send');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [code, setCode] = useState<string>('');
  const [submittingCode, setSubmittingCode] = useState(false);
  const [verificationExpiryMs, setVerificationExpiryMs] = useState<
    number | null
  >(null);

  const prevEmailVerifiedRef = useRef(
    ClaimsLogic.isEmailVerified(contextClaims)
  );
  const isConfirmingCodeRef = useRef(false);

  // Replace any visible toast before presenting the next one. A single
  // useAppToast instance shows one toast at a time, so without this a rapid
  // success→error sequence (e.g. "code sent" then an invalid code) would have
  // the second toast swallowed by the first's lingering overlay.
  const showToast = useCallback(
    (message: string, color: 'success' | 'danger'): void => {
      void dismissToast().then(() =>
        presentToast({ message, color, duration: 3000 })
      );
    },
    [presentToast, dismissToast]
  );

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      // Validate email is available
      if (!contextEmail) {
        throw new Error('Email is required for email verification');
      }

      // Call the API and get the response with verification ID
      const response = await apiCall('/api/idv/verifications', {
        method: 'POST',
        body: JSON.stringify({
          level: CLAIMS_LEVEL.EMAIL,
          email: contextEmail,
          continue: providedContinuePath,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || 'Failed to initiate identity verification'
        );
      }

      const data = await response.json();
      setVerificationId(data.id);
      setVerificationExpiryMs(data.expiresInMs ?? null);

      // Mark that IDV has been initiated
      prevEmailVerifiedRef.current = ClaimsLogic.isEmailVerified(contextClaims);
      setIdvInitiated(true);
      setCurrentPage('code');

      // Show success message about email verification
      showToast(
        'Verification email sent! Please enter the 6-digit code from your email.',
        'success'
      );
    } catch (error) {
      // Handle errors with toast feedback
      showToast(
        error instanceof Error ? error.message : 'An error occurred',
        'danger'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (code.length !== 6) {
      showToast('Please enter a 6-digit code', 'danger');
      return;
    }

    if (!verificationId) {
      showToast('Verification ID is missing. Please try again.', 'danger');
      return;
    }

    isConfirmingCodeRef.current = true;
    setSubmittingCode(true);

    try {
      // Validate and create SixDigitCode
      const sixDigitCode = SixDigitCode.create(code);

      // Confirm verification with code
      await postIdvConfirmVerification(verificationId, undefined, sixDigitCode);

      // Refresh user context to get updated trust level
      await refreshUserContext();

      // Close modal and call success handler
      setCurrentPage('send');
      setCode('');
      setIdvInitiated(false);
      onClose('success');
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // A dropped confirm request (iOS reports "Load failed") is a network
      // problem, not a wrong code — say so instead of showing the raw text (#386).
      showToast(
        isNetworkError(error)
          ? NETWORK_ERROR_MESSAGE
          : error instanceof Error
            ? error.message
            : 'Invalid or expired code',
        'danger'
      );
      setCode(''); // Clear code on error
    } finally {
      isConfirmingCodeRef.current = false;
      setSubmittingCode(false);
    }
  };

  const handleResend = async () => {
    // Reset state and go back to send page
    setCurrentPage('send');
    setCode('');
    setVerificationId(null);
    setIdvInitiated(false);
    // Trigger send again
    await handleSubmit();
  };

  // Monitor claims changes to detect when verification completes
  useEffect(() => {
    if (!isOpen || !idvInitiated) return;

    const currentEmailVerified = ClaimsLogic.isEmailVerified(contextClaims);

    // Email verification just completed
    if (!prevEmailVerifiedRef.current && currentEmailVerified) {
      // Verification completed - close modal and call success handler
      setIdvInitiated(false);
      dismissToast();
      onClose('success');
      if (onSuccess) {
        onSuccess();
      }
    }

    prevEmailVerifiedRef.current = currentEmailVerified;
  }, [isOpen, idvInitiated, contextClaims, onClose, onSuccess, dismissToast]);

  // Periodically refresh user context while waiting for verification
  useEffect(() => {
    if (!isOpen || !idvInitiated) return;

    const intervalId = setInterval(() => {
      if (!isConfirmingCodeRef.current) {
        refreshUserContext().catch(error => {
          // eslint-disable-next-line no-console
          console.error('Failed to refresh user context:', error);
        });
      }
    }, 2000); // Check every 2 seconds

    return () => clearInterval(intervalId);
  }, [isOpen, idvInitiated, refreshUserContext]);

  // Validate email is available when modal opens
  useEffect(() => {
    if (isOpen) {
      if (!contextEmail) {
        throw new Error(
          'EmailVerificationModal cannot be opened without an email address in user context'
        );
      }
    }
  }, [isOpen, contextEmail]);

  // Close modal when backend-reported expiry elapses
  useEffect(() => {
    if (!isOpen || !idvInitiated || verificationExpiryMs == null) return;

    const timeoutId = setTimeout(() => {
      handleClose('expired');
    }, verificationExpiryMs);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, idvInitiated, verificationExpiryMs]);

  // Reset IDV initiated state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIdvInitiated(false);
      setCurrentPage('send');
      setCode('');
      setVerificationId(null);
      setVerificationExpiryMs(null);
      prevEmailVerifiedRef.current = ClaimsLogic.isEmailVerified(contextClaims);
    }
  }, [isOpen, contextClaims]);

  const handleClose = (reason: VerificationModalCloseReason = 'cancelled') => {
    dismissToast();
    setIdvInitiated(false);
    setCurrentPage('send');
    setCode('');
    setVerificationId(null);
    setVerificationExpiryMs(null);
    onClose(reason);
  };

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    if (
      code.length === 6 &&
      currentPage === 'code' &&
      !submittingCode &&
      verificationId
    ) {
      handleCodeSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={() => handleClose('cancelled')}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Email Verification</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {currentPage === 'send' && (
          <div style={{ padding: '20px' }}>
            <IonText>
              <p style={{ fontSize: pxToRem(14) }}>
                We need to send you an email to confirm your email address.
              </p>
            </IonText>
            <IonItem>
              <IonLabel position="stacked">
                <IonIcon icon={mailOutline} style={{ marginRight: '5px' }} />
                Email
              </IonLabel>
              <IonText>
                <p style={{ margin: '8px 0', fontSize: pxToRem(16) }}>
                  {contextEmail || 'No email set'}
                </p>
              </IonText>
            </IonItem>

            <div
              style={{
                padding: '20px 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <IonButton expand="block" onClick={handleSubmit} color="primary">
                {submitting ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <IonIcon icon={checkmarkCircleOutline} />
                )}
                {submitting ? 'Processing...' : 'Send Email'}
              </IonButton>
              <IonButton
                expand="block"
                onClick={() => handleClose('cancelled')}
                color="medium"
              >
                Cancel
              </IonButton>
            </div>
          </div>
        )}

        {currentPage === 'code' && (
          <div style={{ padding: '20px' }}>
            <IonText>
              <p style={{ fontSize: pxToRem(14), marginBottom: '20px' }}>
                Enter the 6-digit verification code sent to your email.
              </p>
            </IonText>

            <div
              style={{
                margin: '20px 0',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <IonInputOtp
                length={6}
                separators={[3]}
                value={code}
                onIonInput={e => {
                  const value = e.detail.value || '';
                  // Only allow digits, max 6
                  const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
                  setCode(digitsOnly);
                }}
                disabled={submittingCode}
              />
            </div>

            <div
              style={{
                padding: '20px 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <IonButton
                expand="block"
                onClick={handleCodeSubmit}
                color="success"
                disabled={code.length !== 6 || submittingCode}
              >
                {submittingCode ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <IonIcon icon={checkmarkCircleOutline} />
                )}
                {submittingCode ? 'Verifying...' : 'Verify Code'}
              </IonButton>
              <IonButton
                expand="block"
                onClick={handleResend}
                color="secondary"
              >
                Resend Email
              </IonButton>
              <IonButton
                expand="block"
                onClick={() => handleClose('cancelled')}
                color="medium"
              >
                Cancel
              </IonButton>
            </div>

            <IonText>
              <p
                style={{
                  fontSize: pxToRem(12),
                  color: '#666',
                  marginTop: '20px',
                  textAlign: 'center',
                }}
              >
                Your email also includes a confirmation link that can be used
                instead of entering the code here.
              </p>
            </IonText>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default EmailVerificationModal;
